import { BulkSampleStatus } from './store/Samples.type';

/**
 * Status colours the Sample To Do list pre-selects in its Sample Status filter when the user
 * has no saved filter — the unresolved work only, so approved (green) and rejected (red)
 * samples stay out of the default view. Both spellings of grey are accepted: the seeded
 * `-` status stores `gray` while the Sample Status master writes `grey`.
 */
export const DEFAULT_SAMPLE_STATUS_COLOURS = ['grey', 'gray', 'amber'];

/**
 * Derives the default Sample Status filter ids from a company's status master. SampleStatus is
 * a per-tenant master, so the ids differ per company and are resolved by colour rather than
 * hardcoded. Returns an empty array when no status matches, which leaves the filter unset.
 *
 * A status whose colour is not set in the master counts as unresolved and is included. The
 * colour is what marks a status as finished work — green for approved, red for rejected — so a
 * status nobody has assigned one to has not been declared finished. Excluding it hid those
 * samples from the default view entirely, which is the wrong way to be wrong: the list exists
 * to surface outstanding work, so an unclassified status belongs in it until someone says
 * otherwise. A status with no `_id` is still dropped — the filter sends ids, so one without an
 * id cannot be expressed.
 * @param statuses - The company's Sample Status master rows.
 * @returns Ids of the statuses to pre-select.
 */
export function getDefaultSampleStatusIds(statuses: BulkSampleStatus[] = []): string[] {
  return statuses
    .filter((status) => {
      const colour = String(status?.statusColor ?? '').trim().toLowerCase();
      if (!colour) {
        return true;
      }
      return DEFAULT_SAMPLE_STATUS_COLOURS.includes(colour);
    })
    .map((status) => status?._id)
    .filter((id): id is string => Boolean(id));
}
